import { computed, Injectable, signal } from '@angular/core';

import {
  DEFAULT_HISTOGRAM_CONFIG,
  type HistogramConfig,
} from '../widgets/statistics/charts/histogram/histogram-chart.model';
import {
  buildGridHighlights,
  buildHistogramOverlay,
  mergeChartSelectionIntoHighlight,
  mergeHoveredValueIntoHighlight,
  type BinSelection,
} from '../widgets/statistics/measures/measures/measures-highlight.util';
import type { MeasureId } from '../widgets/statistics/measures/measures/measures.model';
import { computeAllMeasures, type MeasureValues } from '../widgets/statistics/measures/measures/measures.util';

@Injectable()
export class DescriptiveDashboardStore {
  // ---------------------------------------------------------------------------
  // Writable state
  // ---------------------------------------------------------------------------

  readonly data = signal<number[]>([]);
  readonly histogramConfig = signal<HistogramConfig>(DEFAULT_HISTOGRAM_CONFIG);
  readonly hoveredMeasure = signal<MeasureId | null>(null);
  readonly pinnedMeasures = signal<Set<MeasureId>>(new Set());
  readonly selectedBin = signal<BinSelection | null>(null);
  readonly selectedOutlierValue = signal<number | null>(null);
  readonly hoveredValue = signal<number | null>(null);

  // ---------------------------------------------------------------------------
  // Computed state
  // ---------------------------------------------------------------------------

  readonly sortedData = computed(() => [...this.data()].sort((a, b) => a - b));

  readonly measureValues = computed<MeasureValues>(() => computeAllMeasures(this.data()));

  readonly activeMeasures = computed(() => {
    const set = new Set(this.pinnedMeasures());
    const hovered = this.hoveredMeasure();
    if (hovered) {
      set.add(hovered);
    }
    return set as ReadonlySet<MeasureId>;
  });

  readonly histogramOverlay = computed(() => buildHistogramOverlay(this.activeMeasures(), this.measureValues()));

  readonly gridHighlight = computed(() => {
    const base = buildGridHighlights(this.activeMeasures(), this.measureValues());
    const withSelection = mergeChartSelectionIntoHighlight(base, this.selectedBin(), this.selectedOutlierValue());
    return mergeHoveredValueIntoHighlight(withSelection, this.hoveredValue());
  });

  // ---------------------------------------------------------------------------
  // Methods
  // ---------------------------------------------------------------------------

  setData(data: number[]): void {
    this.data.set(data);
  }

  setHistogramConfig(config: HistogramConfig): void {
    this.histogramConfig.set(config);
  }

  hoverMeasure(id: MeasureId | null): void {
    this.hoveredMeasure.set(id);
  }

  pinMeasure(id: MeasureId): void {
    if (this.pinnedMeasures().has(id)) {
      return;
    }
    const next = new Set(this.pinnedMeasures());
    next.add(id);
    this.pinnedMeasures.set(next);
  }

  unpinMeasure(id: MeasureId): void {
    if (!this.pinnedMeasures().has(id)) {
      return;
    }
    const next = new Set(this.pinnedMeasures());
    next.delete(id);
    this.pinnedMeasures.set(next);
  }

  isPinned(id: MeasureId): boolean {
    return this.pinnedMeasures().has(id);
  }

  hoverValue(value: number | null): void {
    this.hoveredValue.set(value);
  }

  selectBin(bin: BinSelection): void {
    const current = this.selectedBin();
    if (current && current.dataIndex === bin.dataIndex) {
      this.selectedBin.set(null);
    } else {
      this.selectedBin.set(bin);
    }
    this.selectedOutlierValue.set(null);
  }

  selectOutlierValue(value: number): void {
    if (this.selectedOutlierValue() === value) {
      this.selectedOutlierValue.set(null);
    } else {
      this.selectedOutlierValue.set(value);
    }
    this.selectedBin.set(null);
  }
}
